import React, { useState } from 'react';
import { PencilIcon, XIcon, FileTextIcon, LinkIcon } from 'lucide-react';
type Section = 'secteurs' | 'ressources';
interface Zone {
  id: string;
  nomFr: string;
  nomAn: string;
}
interface Ressource {
  id: string;
  titreFr: string;
  titreAn: string;
  type: 'document' | 'lien';
  url: string;
}
const INITIAL_ZONES: Zone[] = [{
  id: '1',
  nomFr: 'Secteurs A-B-E',
  nomAn: 'A-B-E Sectors'
}, {
  id: '2',
  nomFr: 'Secteurs C-I-J-L-M-X-Y',
  nomAn: 'C-I-J-L-M-X-Y Sectors'
}, {
  id: '3',
  nomFr: 'Secteurs N-O-S',
  nomAn: 'N-O-S Sectors'
}, {
  id: '4',
  nomFr: 'Secteurs P-R-T-V',
  nomAn: 'P-R-T-V Sectors'
}];
const INITIAL_RESSOURCES: Ressource[] = [{
  id: '1',
  titreFr: 'Calendrier des collectes 2026',
  titreAn: '2026 Collection Calendar',
  type: 'document',
  url: 'https://www.windsor.ca/calendrier-collectes.pdf'
}, {
  id: '2',
  titreFr: 'Guide du tri des matières résiduelles',
  titreAn: 'Waste Sorting Guide',
  type: 'document',
  url: 'https://www.windsor.ca/guide-tri.pdf'
}, {
  id: '3',
  titreFr: 'Trouver mon secteur de collecte',
  titreAn: 'Find My Collection Sector',
  type: 'lien',
  url: 'https://images.app.goo.gl/PbPhBmsQyacfdbZu8'
}];
export function CollectesConfigurer() {
  const [activeSection, setActiveSection] = useState<Section>('secteurs');
  // ── Secteurs (Zones) ──────────────────────────────────────
  const [zones, setZones] = useState<Zone[]>(INITIAL_ZONES);
  const [editingZoneId, setEditingZoneId] = useState<string | null>(null);
  const [zoneForm, setZoneForm] = useState({
    nomFr: '',
    nomAn: ''
  });
  const [lienSecteurs, setLienSecteurs] = useState('https://images.app.goo.gl/PbPhBmsQyacfdbZu8');
  const handleEditZone = (zone: Zone) => {
    setEditingZoneId(zone.id);
    setZoneForm({
      nomFr: zone.nomFr,
      nomAn: zone.nomAn
    });
  };
  const handleSaveZone = (id: string) => {
    setZones(zones.map(z => z.id === id ? {
      ...z,
      ...zoneForm
    } : z));
    setEditingZoneId(null);
  };
  const handleDeleteZone = (id: string) => {
    setZones(zones.filter(z => z.id !== id));
  };
  const handleAddZone = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    setZones([{
      id: newId,
      nomFr: '',
      nomAn: ''
    }, ...zones]);
    setEditingZoneId(newId);
    setZoneForm({
      nomFr: '',
      nomAn: ''
    });
  };
  // ── Ressources ────────────────────────────────────────────
  const [ressources, setRessources] = useState<Ressource[]>(INITIAL_RESSOURCES);
  const [editingResId, setEditingResId] = useState<string | null>(null);
  const [resForm, setResForm] = useState<{
    titreFr: string;
    titreAn: string;
    type: 'document' | 'lien';
    url: string;
  }>({
    titreFr: '',
    titreAn: '',
    type: 'document',
    url: ''
  });
  const handleEditRes = (res: Ressource) => {
    setEditingResId(res.id);
    setResForm({
      titreFr: res.titreFr,
      titreAn: res.titreAn,
      type: res.type,
      url: res.url
    });
  };
  const handleSaveRes = (id: string) => {
    setRessources(ressources.map(r => r.id === id ? {
      ...r,
      ...resForm
    } : r));
    setEditingResId(null);
  };
  const handleDeleteRes = (id: string) => {
    setRessources(ressources.filter(r => r.id !== id));
  };
  const handleAddRes = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    setRessources([{
      id: newId,
      titreFr: '',
      titreAn: '',
      type: 'document',
      url: ''
    }, ...ressources]);
    setEditingResId(newId);
    setResForm({
      titreFr: '',
      titreAn: '',
      type: 'document',
      url: ''
    });
  };
  // ── Secteurs section render ───────────────────────────────
  const renderSecteurs = () => <div className="space-y-6" data-id="element-1395" data-mp="1gvwmmudqq6s2">
      <div className="flex items-center justify-between" data-id="element-1396" data-mp="3doon17tfb8tv">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1397" data-mp="3dq9hyyjngts9">Secteurs</h3>
        <button onClick={handleAddZone} className="bg-[#4F46E5] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#4338CA] transition-colors" data-id="element-1398" data-mp="3imzx9jnmrlcm">
          Ajouter une zone
        </button>
      </div>

      <div className="space-y-4" data-id="element-1399" data-mp="17zcw9zalho0c">
        {zones.map(zone => <div key={zone.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-id="element-1400" data-mp="182sevc3h9f6i">
            {editingZoneId === zone.id ? <div className="p-6 relative" data-id="element-1401" data-mp="1hyc5biz9vlnv">
                <button onClick={() => {
            if (!zone.nomFr && !zone.nomAn) handleDeleteZone(zone.id);else setEditingZoneId(null);
          }} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" aria-label="Fermer" data-id="element-1402" data-mp="1hzdknznbmube">
                  <XIcon className="w-5 h-5" data-id="element-1403" data-mp="131uxv2u7er6l" />
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1404" data-mp="1335vsm0yx4ps">
                  <div data-id="element-1405" data-mp="y9biq1fvbnzo">
                    <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1406" data-mp="al0o3g0rmkju">
                      Nom de la zone FR
                    </label>
                    <input type="text" value={zoneForm.nomFr} onChange={e => setZoneForm({
                ...zoneForm,
                nomFr: e.target.value
              })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-1407" data-mp="6infmjumw8jd" />
                  </div>
                  <div data-id="element-1408" data-mp="3nlt4znjiv1yj">
                    <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1409" data-mp="3nl0596izdcpw">
                      Nom de la zone AN
                    </label>
                    <input type="text" value={zoneForm.nomAn} onChange={e => setZoneForm({
                ...zoneForm,
                nomAn: e.target.value
              })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-1410" data-mp="f3x0svbig9fx" />
                  </div>
                </div>
                <div className="flex justify-end" data-id="element-1411" data-mp="2pwhm2ng1gxhs">
                  <button onClick={() => handleSaveZone(zone.id)} className="border border-[#4F46E5] text-[#4F46E5] px-4 py-1.5 rounded-md text-sm font-medium hover:bg-indigo-50 transition-colors" data-id="element-1412" data-mp="2uwvx8ecxsnk1">
                    Sauvegarder
                  </button>
                </div>
              </div> : <div className="px-4 py-4 flex items-center justify-between" data-id="element-1413" data-mp="whoffy530bqw">
                <span className="text-sm text-slate-700" data-id="element-1414" data-mp="rmr94yz6sghp">{zone.nomFr}</span>
                <div className="flex items-center gap-3" data-id="element-1415" data-mp="moppocxumf5p">
                  <button onClick={() => handleEditZone(zone)} className="text-slate-400 hover:text-slate-600" aria-label="Modifier" data-id="element-1416" data-mp="hq62683lwvb8">
                    <PencilIcon className="w-4 h-4" data-id="element-1417" data-mp="do19bbkzg2pn" />
                  </button>
                  <button onClick={() => handleDeleteZone(zone.id)} className="text-slate-400 hover:text-slate-600" aria-label="Supprimer" data-id="element-1418" data-mp="8oy33nt9nqg3">
                    <XIcon className="w-4 h-4" data-id="element-1419" data-mp="3shfqvtw5g42" />
                  </button>
                </div>
              </div>}
          </div>)}
      </div>

      <div className="mt-8" data-id="element-1420" data-mp="1y4xj9ovayg58">
        <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1421" data-mp="1y5pfn1wq4hwk">
          Lien vers les secteurs
        </label>
        <input type="text" value={lienSecteurs} onChange={e => setLienSecteurs(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-1422" data-mp="25nx0bnvajvlf" />
      </div>

      <div className="flex justify-end mt-8" data-id="element-1423" data-mp="2kdmj0wzfgi9g">
        <button className="bg-[#4F46E5] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#4338CA] transition-colors" data-id="element-1424" data-mp="2kcbaykyy8ocj">
          Sauvegarder
        </button>
      </div>
    </div>;
  // ── Ressources section render ─────────────────────────────
  const renderRessources = () => <div className="space-y-6" data-id="element-1425" data-mp="1wlxiwugd6phy">
      <div className="flex items-center justify-between" data-id="element-1426" data-mp="1wpv3zlmi5r4h">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1427" data-mp="21nd36kapwrza">Ressources</h3>
        <button onClick={handleAddRes} className="bg-[#4F46E5] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#4338CA] transition-colors" data-id="element-1428" data-mp="38373xlf55pld">
          Ajouter une ressource
        </button>
      </div>

      <p className="text-sm text-slate-500" data-id="element-1429" data-mp="21ge9t237op4">
        Documents et liens utiles partagés aux citoyens (calendriers, guides de
        tri, page de recherche de secteur, etc.).
      </p>

      <div className="space-y-4" data-id="element-1430" data-mp="bqhtvi4q042k">
        {ressources.map(res => <div key={res.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-id="element-1431" data-mp="bs1c3e9xsp6z">
            {editingResId === res.id ? <div className="p-6 relative" data-id="element-1432" data-mp="kqdaw3kyitai">
                <button onClick={() => {
            if (!res.titreFr && !res.url) handleDeleteRes(res.id);else setEditingResId(null);
          }} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" aria-label="Fermer" data-id="element-1433" data-mp="kpbq37e52sfl">
                  <XIcon className="w-5 h-5" data-id="element-1434" data-mp="pq93l6o7eyar" />
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1435" data-mp="pnwnkz20ay0c">
                  <div data-id="element-1436" data-mp="2wgz4p72xnpm2">
                    <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1437" data-mp="2wijpi4zg3u6q">
                      Titre FR
                    </label>
                    <input type="text" value={resForm.titreFr} onChange={e => setResForm({
                ...resForm,
                titreFr: e.target.value
              })} placeholder="Titre de la ressource en Français" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-1438" data-mp="16z1vu5kfaquo" />
                  </div>
                  <div data-id="element-1439" data-mp="o8kip9c2q3uf">
                    <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1440" data-mp="o80x9in7sxbn">
                      Titre AN
                    </label>
                    <input type="text" value={resForm.titreAn} onChange={e => setResForm({
                ...resForm,
                titreAn: e.target.value
              })} placeholder="Titre de la ressource en Anglais" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-1441" data-mp="eanzdu8zg2l8" />
                  </div>
                  <div data-id="element-1442" data-mp="a99qy721btiw">
                    <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1443" data-mp="a8qbqy3rrj8k">
                      Type
                    </label>
                    <select value={resForm.type} onChange={e => setResForm({
                ...resForm,
                type: e.target.value as 'document' | 'lien'
              })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-1444" data-mp="f1azpizzv246">
                      <option value="document" data-id="element-1445" data-mp="xyklh6sskrol">Document</option>
                      <option value="lien" data-id="element-1446" data-mp="xy2og7yb29fm">Lien</option>
                    </select>
                  </div>
                  <div data-id="element-1447" data-mp="j3o3w2z3eryu">
                    <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1448" data-mp="o0eismz5vhio">
                      URL
                    </label>
                    <input type="text" value={resForm.url} onChange={e => setResForm({
                ...resForm,
                url: e.target.value
              })} placeholder="https://..." className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-1449" data-mp="33r13ialfahi6" />
                  </div>
                </div>
                <div className="flex justify-end" data-id="element-1450" data-mp="1zsxd4j4xyco8">
                  <button onClick={() => handleSaveRes(res.id)} className="border border-[#4F46E5] text-[#4F46E5] px-4 py-1.5 rounded-md text-sm font-medium hover:bg-indigo-50 transition-colors" data-id="element-1451" data-mp="1zrma4ljbl8y6">
                    Sauvegarder
                  </button>
                </div>
              </div> : <div className="px-4 py-4 flex items-center justify-between" data-id="element-1452" data-mp="131ld5z8srm5s">
                <div className="flex items-center gap-3 min-w-0" data-id="element-1453" data-mp="1gvw48n89nsf0">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0" data-id="element-1454" data-mp="1c3um4kof37h2">
                    {res.type === 'document' ? <FileTextIcon className="w-4 h-4" data-id="element-1455" data-mp="ocp5w4eked68" /> : <LinkIcon className="w-4 h-4" data-id="element-1456" data-mp="kdq7z3r93goa" />}
                  </div>
                  <div className="min-w-0" data-id="element-1457" data-mp="2jhycjow1br1r">
                    <p className="text-sm font-medium text-slate-700 truncate" data-id="element-1458" data-mp="2jgx31ouo3cm5">
                      {res.titreFr}
                    </p>
                    <p className="text-xs text-slate-400 truncate" data-id="element-1459" data-mp="2oeylq8o6l9x3">{res.url}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-4" data-id="element-1460" data-mp="2y6l3knv0y78t">
                  <button onClick={() => handleEditRes(res)} className="text-slate-400 hover:text-slate-600" aria-label="Modifier" data-id="element-1461" data-mp="2y8p4qy1jgs5e">
                    <PencilIcon className="w-4 h-4" data-id="element-1462" data-mp="25hnkwjmwi8fj" />
                  </button>
                  <button onClick={() => handleDeleteRes(res.id)} className="text-slate-400 hover:text-slate-600" aria-label="Supprimer" data-id="element-1463" data-mp="2ah9yiu6n5qov">
                    <XIcon className="w-4 h-4" data-id="element-1464" data-mp="2fhed2enzobcg" />
                  </button>
                </div>
              </div>}
          </div>)}
      </div>

      <div className="flex justify-end mt-8" data-id="element-1465" data-mp="3lyjj5k1iaorr">
        <button className="bg-[#4F46E5] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#4338CA] transition-colors" data-id="element-1466" data-mp="3quqnj8e97s7l">
          Sauvegarder
        </button>
      </div>
    </div>;
  return <div className="flex gap-6" data-id="element-1467" data-mp="1a8trs5af38g5">
      {/* Section Switcher */}
      <div className="w-48 flex-shrink-0" data-id="element-1468" data-mp="15idd6huv1gs7">
        <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 sticky top-4" data-id="element-1469" data-mp="15hbq1f0kl3u4">
          <button onClick={() => setActiveSection('secteurs')} className={`w-full px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${activeSection === 'secteurs' ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-1470" data-mp="10i8waqq1ik1g">
            Secteurs
          </button>
          <button onClick={() => setActiveSection('ressources')} className={`w-full px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${activeSection === 'ressources' ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-1471" data-mp="mkalfvaxh5qp">
            Ressources
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8 min-w-0" data-id="element-1472" data-mp="19kricjfqcbzf">
        {activeSection === 'secteurs' ? renderSecteurs() : renderRessources()}
      </div>
    </div>;
}